import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import type { ClubBillingDto } from "@tc/shared";
import { Button, Card, ClubAvatar, Icon, Tag } from "@/components/ui";
import { Can } from "@/lib/auth/authz";
import { useClubs } from "@/lib/clubs/clubs";
import { adminApi } from "./api";
import { ClubUsersSection } from "./ClubUsersSection";

/**
 * Back-office club detail (view). Shows the club's profile and short code, its read-only invoice
 * details (the active billing version), and the people who can sign in to its portal. "Edit details"
 * opens the full edit form. Editing the invoice details still happens there (and in the club's own
 * Settings → Billing); this page only reads them.
 */
export function ClubDetailPage() {
  const { t } = useTranslation();
  const { clubId = "" } = useParams();
  const navigate = useNavigate();
  const { clubs } = useClubs();
  const club = clubs.find((c) => c.id === clubId);

  const [billing, setBilling] = useState<ClubBillingDto | null>(null);

  const loadBilling = useCallback(async () => {
    try {
      setBilling(await adminApi.getClubBilling(clubId));
    } catch {
      /* billing card falls back to "Not set" */
    }
  }, [clubId]);

  useEffect(() => {
    void loadBilling();
  }, [loadBilling]);

  if (!club) {
    return (
      <div className="page">
        <div className="empty">{t("common.comingSoon")}</div>
      </div>
    );
  }

  return (
    <div className="page">
      <button type="button" className="link admin-back" onClick={() => navigate("/clubs")}>
        <Icon name="chevD" size={14} style={{ transform: "rotate(90deg)" }} /> {t("clubs.backToClubs")}
      </button>

      <div className="page-head row-between club-detail-head">
        <div className="club-detail-id">
          <ClubAvatar name={club.name} seed={club.id} lg />
          <div>
            <div className="club-detail-title">
              <h1>{club.name}</h1>
              <Tag variant="neutral">{club.shortCode}</Tag>
            </div>
            <div className="sub">{t("clubs.tripsSummary", { trips: club.tripCount, open: club.openTripCount })}</div>
          </div>
        </div>
        <Can permission="club.manage">
          <Button variant="secondary" icon="edit" onClick={() => navigate(`/clubs/${club.id}/edit`)}>
            {t("clubs.editDetails")}
          </Button>
        </Can>
      </div>

      <Card>
        <div className="set-section-head">
          <h2>{t("clubs.profileTitle")}</h2>
          <div className="sub">{t("clubs.profileSub")}</div>
        </div>
        <div className="club-view-grid" style={{ marginTop: 18 }}>
          <ViewField label={t("clubs.name")} value={club.name} />
          <ViewField label={t("clubs.shortCode")} value={club.shortCode} />
          <ViewField label={t("clubs.primaryContact")} value={club.primaryContact} />
          <ViewField label={t("clubs.contactPhone")} value={club.contactPhone} />
          <ViewField label={t("clubs.contactEmail")} value={club.contactEmail} link />
        </div>
      </Card>

      <Card style={{ marginTop: 18 }}>
        <div className="set-section-head">
          <h2>{t("clubs.invoiceTitle")}</h2>
          <div className="sub">{t("clubs.invoiceSub")}</div>
        </div>
        <div className="club-view-grid" style={{ marginTop: 18 }}>
          <ViewField label={t("settings.billing.companyName")} value={billing?.companyName} />
          <ViewField label={t("settings.billing.taxId")} value={billing?.taxId} />
        </div>
        <div className="cv-field" style={{ marginTop: 18 }}>
          <span className="cv-label">{t("settings.billing.address.title")}</span>
          <BillingAddress billing={billing} />
        </div>
        <div className="cv-field" style={{ marginTop: 18 }}>
          <span className="cv-label">{t("settings.billing.accounting.title")}</span>
          <ViewField label={t("settings.billing.accountingEmail")} value={billing?.accountingEmail} link />
        </div>
      </Card>

      <div style={{ marginTop: 18 }}>
        <ClubUsersSection club={club} />
      </div>
    </div>
  );
}

function ViewField({ label, value, link }: { label: string; value?: string; link?: boolean }) {
  const { t } = useTranslation();
  const has = Boolean(value && value.trim());
  return (
    <div className="cv-field">
      <span className="cv-label">{label}</span>
      {has ? (
        <span className={link ? "cv-value cv-link" : "cv-value"}>{value}</span>
      ) : (
        <span className="cv-value muted">{t("clubs.notSet")}</span>
      )}
    </div>
  );
}

function BillingAddress({ billing }: { billing: ClubBillingDto | null }) {
  const { t } = useTranslation();
  const lines = [
    billing?.street?.trim(),
    [billing?.postalCode?.trim(), billing?.city?.trim()].filter(Boolean).join(" "),
    billing?.province?.trim(),
  ].filter((l): l is string => Boolean(l));

  if (lines.length === 0) {
    return <span className="cv-value muted">{t("clubs.notSet")}</span>;
  }
  return (
    <div className="cv-address">
      {lines.map((line, i) => (
        <div key={i}>{line}</div>
      ))}
    </div>
  );
}
